import { createFormControl } from 'react-hook-form';
import { ApplicantsFormDataValues } from './schema';
import { useCallback, useMemo } from 'react';
import { SelectedApplicantTab, useApplicantsFormStore } from './store';
import {
  ace_hooks,
  debt_register_hooks,
  payment_remark_hooks,
  useCreateCustomer,
  useCreatePerson,
  useDeleteCoApplicant,
  useLoanApplication,
  usePatchPerson,
  useUpdateApplicationDetails,
  useUpdateCustomer,
} from '@axo/shared/data-access/hooks';
import { AppToaster } from '../../components/AppToaster';
import { event_log, Product } from '@axo/shared/data-access/types';
import {
  KnownResourceType,
  useEventLogger,
} from '@axo/shared/data-access/event-log';
import { useApplicantsFormDefaultValues } from './defaultValues';

function stringifyField(value: unknown) {
  if (typeof value === 'string') return value;

  return JSON.stringify(value);
}

function buildFieldDiff(
  dirtyFields: string[],
  oldValues: Record<string, unknown> | undefined,
  newValues: Record<string, unknown> | undefined,
): Record<string, string> {
  return Object.fromEntries(
    dirtyFields.flatMap((key) => [
      [`old${key}`, stringifyField(oldValues?.[key])],
      [`new${key}`, stringifyField(newValues?.[key])],
    ]),
  );
}

export const useApplicantsFormOnSubmitFactory = ({
  applicationID,
  formContext,
}: {
  applicationID: string;
  formContext: ReturnType<typeof createFormControl<ApplicantsFormDataValues>>;
}) => {
  const {
    actions: { setIsEditing, setSelectedApplicant },
  } = useApplicantsFormStore(applicationID);
  const _defaultValues = useApplicantsFormDefaultValues(applicationID);

  const { data: applicationData } = useLoanApplication(applicationID);
  const { mutateAsync: patchPerson } = usePatchPerson();
  const { mutateAsync: patchLoanApplication } = useUpdateApplicationDetails();
  const { mutateAsync: createCustomer } = useCreateCustomer();
  const { mutateAsync: patchCustomer } = useUpdateCustomer();
  const { mutateAsync: createPerson } = useCreatePerson();
  const { mutateAsync: triggerCreditCheck } =
    payment_remark_hooks.useTriggerCreditCheck();
  const { mutateAsync: updateDebtRegister } =
    debt_register_hooks.useUpdateDebtRegistryData();
  const isSecuredLending = applicationData?.Product === Product.SecuredLoan;
  const { data: axoVersionData } = ace_hooks.useAxoVersion(
    isSecuredLending ? applicationID : undefined,
  );
  const { mutateAsync: createAxoVersion } = ace_hooks.useCreateAxoVersion();
  const { mutateAsync: deleteCoApplicant } = useDeleteCoApplicant();

  const mainApplicantTags: event_log.Resource[] = useMemo(
    () => [
      {
        Type: KnownResourceType.LoanApplication,
        ID: applicationID,
      },
      {
        Type: KnownResourceType.Person,
        ID: applicationData?.PersonID ?? '',
      },
      {
        Type: KnownResourceType.Customer,
        ID: applicationData?.CustomerID ?? '',
      },
    ],
    [applicationID, applicationData?.PersonID, applicationData?.CustomerID],
  );

  const coApplicantTags: event_log.Resource[] = useMemo(
    () => [
      {
        Type: KnownResourceType.LoanApplication,
        ID: applicationID,
      },
      {
        Type: KnownResourceType.Person,
        ID: applicationData?.CoApplicantPersonID ?? '',
      },
      {
        Type: KnownResourceType.Customer,
        ID: applicationData?.CoApplicantCustomerID ?? '',
      },
    ],
    [
      applicationID,
      applicationData?.CoApplicantPersonID,
      applicationData?.CoApplicantCustomerID,
    ],
  );

  const mainApplicantLogger = useEventLogger(...mainApplicantTags);
  const coApplicantLogger = useEventLogger(...coApplicantTags);

  const onSubmit = useCallback(async () => {
    try {
      // Saving / cloning these values to avoid race conditons
      const values = structuredClone(formContext.getValues());
      const defaultValues = structuredClone(_defaultValues);
      const marketCountry = applicationData?.MarketCountry;
      const broker = applicationData?.Meta?.Broker;
      const mainApplicantPersonID = applicationData?.PersonID;
      const mainApplicantCustomerID = applicationData?.CustomerID;
      const coApplicantPersonID = applicationData?.CoApplicantPersonID;
      const coApplicantCustomerID = applicationData?.CoApplicantCustomerID;
      const applicationRevision = applicationData?.Revision ?? 0;

      const shouldCreateCoApplicant =
        values.CoApplicant &&
        !coApplicantPersonID &&
        !coApplicantCustomerID &&
        (formContext.getFieldState('CoApplicant.Person.SocialSecurityNumber')
          .isDirty ||
          formContext.getFieldState('CoApplicant.Customer.Email').isDirty);
      const shouldRemoveCoApplicant =
        !shouldCreateCoApplicant &&
        (coApplicantPersonID || coApplicantCustomerID) &&
        !values.CoApplicant;

      const loanApplicationDirtyFields = Object.keys(
        values.LoanApplication,
      ).filter(
        (key) =>
          formContext.getFieldState(`LoanApplication.${key}` as never).isDirty,
      );
      const isLoanApplicationDirty = loanApplicationDirtyFields.length > 0;

      const mainApplicantPersonDirtyFields = Object.keys(
        values.MainApplicant.Person,
      ).filter(
        (key) =>
          formContext.getFieldState(`MainApplicant.Person.${key}` as never)
            .isDirty,
      );
      const isMainApplicantPersonDirty =
        mainApplicantPersonDirtyFields.length > 0;

      const coApplicantPersonDirtyFields = Object.keys(
        values.CoApplicant?.Person ?? {},
      ).filter(
        (key) =>
          formContext.getFieldState(`CoApplicant.Person.${key}` as never)
            .isDirty,
      );
      const isCoApplicantPersonDirty = coApplicantPersonDirtyFields.length > 0;

      const mainApplicantCustomerDirtyFields = Object.keys(
        values.MainApplicant.Customer,
      ).filter(
        (key) =>
          formContext.getFieldState(`MainApplicant.Customer.${key}` as never)
            .isDirty,
      );
      const isMainApplicantCustomerDirty =
        mainApplicantCustomerDirtyFields.length > 0;

      const coApplicantCustomerDirtyFields = Object.keys(
        values.CoApplicant?.Customer ?? {},
      ).filter(
        (key) =>
          formContext.getFieldState(`CoApplicant.Customer.${key}` as never)
            .isDirty,
      );
      const isCoApplicantCustomerDirty =
        coApplicantCustomerDirtyFields.length > 0;

      const mainApplicantPersonPatchTask = (async () => {
        if (!isMainApplicantPersonDirty) return;

        if (mainApplicantPersonID) {
          await patchPerson({
            id: mainApplicantPersonID,
            data: values.MainApplicant.Person,
          });
        } else {
          const person = await createPerson({
            MarketCountry: marketCountry,
            ...values.MainApplicant.Person,
          });

          await patchLoanApplication({
            applicationID,
            patch: {
              PersonID: person.ID,
            },
          });
        }

        mainApplicantLogger(
          'patch-main-applicant-person',
          buildFieldDiff(
            mainApplicantPersonDirtyFields,
            defaultValues.MainApplicant.Person,
            values.MainApplicant.Person,
          ),
        );
      })();

      const coApplicantPersonPatchTask = (async () => {
        if (
          shouldCreateCoApplicant ||
          shouldRemoveCoApplicant ||
          !values.CoApplicant ||
          !isCoApplicantPersonDirty
        )
          return;

        if (coApplicantPersonID) {
          await patchPerson({
            id: coApplicantPersonID,
            data: values.CoApplicant.Person,
          });
        } else {
          const person = await createPerson({
            MarketCountry: marketCountry,
            ...values.CoApplicant.Person,
          });

          await patchLoanApplication({
            applicationID,
            patch: {
              CoApplicantPersonID: person.ID,
            },
          });
        }

        coApplicantLogger(
          'patch-co-applicant-person',
          buildFieldDiff(
            coApplicantPersonDirtyFields,
            defaultValues.CoApplicant?.Person,
            values.CoApplicant?.Person,
          ),
        );
      })();

      const patchLoanApplicationTask = (async () => {
        if (!isLoanApplicationDirty) return;

        await patchLoanApplication({
          applicationID,
          patch: values.LoanApplication,
        });
      })();

      const patchMainApplicantCustomerTask = (async () => {
        if (!isMainApplicantCustomerDirty) return;

        const customer = await createCustomer({
          MarketCountry: marketCountry,
          Email: values.MainApplicant.Customer.Email,
        });

        if (!customer.ID)
          throw new Error('Failed to update main applicant (customer entity)');

        await patchCustomer({
          customerID: customer.ID,
          customerEdits: {
            MobilePhoneCountryCode:
              values.MainApplicant.Customer.MobilePhone.CountryCode,
            MobilePhoneNumber: values.MainApplicant.Customer.MobilePhone.Number,
          },
        });

        if (customer.ID !== mainApplicantCustomerID) {
          await patchLoanApplication({
            applicationID,
            patch: {
              CustomerID: customer.ID,
            },
          });
        }

        mainApplicantLogger(
          'patch-main-applicant-customer',
          buildFieldDiff(
            mainApplicantCustomerDirtyFields,
            defaultValues.MainApplicant.Customer,
            values.MainApplicant.Customer,
          ),
        );
      })();

      const patchCoApplicantCustomerTask = (async () => {
        if (
          shouldCreateCoApplicant ||
          shouldRemoveCoApplicant ||
          !values.CoApplicant ||
          !isCoApplicantCustomerDirty
        )
          return;

        const customer = await createCustomer({
          MarketCountry: marketCountry,
          Email: values.CoApplicant.Customer.Email,
        });

        if (!customer.ID)
          throw new Error('Failed to update co-applicant (customer entity)');

        await patchCustomer({
          customerID: customer.ID,
          customerEdits: {
            MobilePhoneCountryCode:
              values.CoApplicant.Customer.MobilePhone.CountryCode,
            MobilePhoneNumber: values.CoApplicant.Customer.MobilePhone.Number,
          },
        });

        if (customer.ID !== coApplicantCustomerID) {
          await patchLoanApplication({
            applicationID,
            patch: {
              CoApplicantCustomerID: customer.ID,
            },
          });
        }

        coApplicantLogger(
          'patch-co-applicant-customer',
          buildFieldDiff(
            coApplicantCustomerDirtyFields,
            defaultValues.CoApplicant?.Customer,
            values.CoApplicant?.Customer,
          ),
        );
      })();

      const createCoApplicantTask = (async () => {
        if (!shouldCreateCoApplicant || !values.CoApplicant) return;

        const customer = await createCustomer({
          MarketCountry: marketCountry,
          Email: values.CoApplicant.Customer.Email,
          MobilePhoneCountryCode:
            values.CoApplicant.Customer.MobilePhone.CountryCode,
          MobilePhoneNumber: values.CoApplicant.Customer.MobilePhone.Number,
        });

        if (!customer.ID)
          throw new Error('Failed to create co-applicant (customer entity)');
        if (customer.ID === mainApplicantCustomerID)
          throw new Error(
            "Co-applicant can't be the same customer as the main applicant",
          );

        await patchCustomer({
          customerID: customer.ID,
          customerEdits: {
            MobilePhoneCountryCode:
              values.CoApplicant.Customer.MobilePhone.CountryCode,
            MobilePhoneNumber: values.CoApplicant.Customer.MobilePhone.Number,
          },
        });

        const person = await createPerson({
          MarketCountry: marketCountry,
          SocialSecurityNumber: values.CoApplicant.Person.SocialSecurityNumber,
        });

        if (!person.ID)
          throw new Error('Failed to create co-applicant (person entity)');
        if (person.ID === mainApplicantPersonID)
          throw new Error(
            "Co-applicant can't be the same person as the main applicant",
          );

        if (isSecuredLending && !axoVersionData) {
          await createAxoVersion({
            LoanApplicationID: applicationID,
            OriginalRevision: applicationRevision,
          });
        }

        await patchLoanApplication({
          applicationID,
          patch: {
            CoApplicantCustomerID: customer.ID,
            CoApplicantPersonID: person.ID,
          },
        });

        setSelectedApplicant(SelectedApplicantTab.CO_APPLICANT);
        coApplicantLogger(
          'add-co-applicant',
          Object.fromEntries([
            ...Object.entries(values.CoApplicant.Person).map(([key, value]) => [
              `person${key}`,
              stringifyField(value),
            ]),
            ...Object.entries(values.CoApplicant.Customer).map(
              ([key, value]) => [`customer${key}`, stringifyField(value)],
            ),
          ]),
        );

        if (broker === undefined) throw new Error('Invalid broker');
        if (!marketCountry) throw new Error('Invalid market country');

        await triggerCreditCheck({
          personID: person.ID,
          broker,
          loanApplicationID: applicationID,
          marketCountry,
        });

        AppToaster.then((toaster) =>
          toaster.show({
            intent: 'primary',
            message: 'Triggered credit check for co-applicant',
          }),
        );

        try {
          await updateDebtRegister({
            applicationID,
            SSN: person.SocialSecurityNumber,
          });

          AppToaster.then((toaster) =>
            toaster.show({
              intent: 'primary',
              message: 'Triggered debt register update for co-applicant',
            }),
          );
        } catch (e) {
          if (e instanceof Error) {
            AppToaster.then((toaster) =>
              toaster.show({
                intent: 'danger',
                message: e.message,
              }),
            );
          }
        }
      })();

      const removeCoApplicantTask = (async () => {
        if (!shouldRemoveCoApplicant) return;

        if (isSecuredLending && !axoVersionData) {
          await createAxoVersion({
            LoanApplicationID: applicationID,
            OriginalRevision: applicationRevision,
          });
        }

        await deleteCoApplicant(applicationID);

        setSelectedApplicant(SelectedApplicantTab.MAIN_APPLICANT);
        coApplicantLogger('removed-co-applicant');
      })();

      await Promise.all([
        mainApplicantPersonPatchTask,
        coApplicantPersonPatchTask,
        patchLoanApplicationTask,
        patchMainApplicantCustomerTask,
        patchCoApplicantCustomerTask,
        createCoApplicantTask,
        removeCoApplicantTask,
      ]);
    } catch (e) {
      if (e instanceof Error) {
        AppToaster.then((toaster) =>
          toaster.show({
            intent: 'danger',
            message: e.message,
          }),
        );
      } else if (typeof e === 'object' && e && 'message' in e) {
        const message = e.message;

        AppToaster.then((toaster) =>
          toaster.show({
            intent: 'danger',
            message:
              typeof message === 'string'
                ? message
                : 'An error has occurred while trying to edit applicant data',
          }),
        );
      }

      throw e;
    } finally {
      setIsEditing(false);
      formContext.reset();
      formContext.trigger();
    }
  }, [
    formContext,
    applicationData,
    setIsEditing,
    patchPerson,
    applicationID,
    patchLoanApplication,
    createCustomer,
    patchCustomer,
    createPerson,
    triggerCreditCheck,
    updateDebtRegister,
    createAxoVersion,
    isSecuredLending,
    axoVersionData,
    setSelectedApplicant,
    mainApplicantLogger,
    coApplicantLogger,
    deleteCoApplicant,
    _defaultValues,
  ]);

  return useMemo(
    () => formContext.handleSubmit(onSubmit, onSubmit),
    [formContext, onSubmit],
  );
};
